'use client';

import { useRef } from 'react';
import type { InspectionEvent } from 'xstate';
import { createInspectorEntryBuilder } from '../entryBuilder';
import type { InspectorEntry } from '../protocol';
import type { UseVoiceLiveTelemetryResult } from '../../hooks/useVoiceLiveTelemetry';

/** Safety valve: a pathological reconnect loop must not bill unbounded telemetry. */
const MAX_EVENTS_PER_SESSION = 1_000;
/** Bounded so a long outage cannot grow the replay buffer without limit. */
const MAX_REPLAY_ENTRIES = 100;

const isNetworkOffline = (entry: InspectorEntry) =>
  typeof entry.state !== 'string' && entry.state.network === 'offline';

export interface MachineEventRecorder {
  /** Feed the raw XState inspection stream. */
  inspect: (inspectionEvent: InspectionEvent) => void;
}

/**
 * Ships every machine event to App Insights as it happens.
 *
 * Nothing is buffered or sampled: an event that never leaves the page is an event lost
 * to the crash that made it interesting, and "is this session worth keeping?" cannot be
 * answered before the bug you have not seen yet shows up.
 *
 * Events emitted while the machine reports the network down are re-sent on reconnect.
 * The SDK exhausts its retry budget during an outage and discards the batch, which loses
 * precisely the events that explain the outage. Replays are idempotent: `seq` identifies
 * them and the inspector import collapses duplicates.
 */
export const useMachineEventTelemetry = (
  telemetry: UseVoiceLiveTelemetryResult,
): MachineEventRecorder => {
  const telemetryRef = useRef(telemetry);
  telemetryRef.current = telemetry;

  const builderRef = useRef(createInspectorEntryBuilder());
  const startedAtRef = useRef(0);
  const seenRef = useRef(0);
  const offlineRef = useRef<InspectorEntry[]>([]);

  const send = (entry: InspectorEntry, replayed = false) => {
    telemetryRef.current.trackMachineEntry(entry, entry.timestamp - startedAtRef.current, replayed);
  };

  const inspect = (inspectionEvent: InspectionEvent) => {
    const entry = builderRef.current.build(inspectionEvent);
    if (!entry) return;

    if (seenRef.current === 0) startedAtRef.current = entry.timestamp;
    seenRef.current += 1;

    if (seenRef.current > MAX_EVENTS_PER_SESSION) {
      if (seenRef.current === MAX_EVENTS_PER_SESSION + 1) {
        telemetryRef.current.trackDiagnostic('Machine', 'Truncated', {
          limit: MAX_EVENTS_PER_SESSION,
          seq: entry.sequence,
        });
      }
      return;
    }

    send(entry);

    if (isNetworkOffline(entry)) {
      offlineRef.current.push(entry);
      while (offlineRef.current.length > MAX_REPLAY_ENTRIES) offlineRef.current.shift();
      return;
    }

    const backlog = offlineRef.current;
    if (backlog.length === 0) return;
    offlineRef.current = [];
    backlog.forEach((buffered) => send(buffered, true));
  };

  return { inspect };
};
